import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { tiposConteudoLabel } from '../../Enum/tipoConteudos';
import { tiposArquivosLabel } from '../../Enum/tipoArquivos';
import './index.css';

const CadListaConteudos = () => {
    const [tipoSelecionado, setTipoSelecionado] = useState('');
    const [conteudos, setConteudos] = useState([]);
    const [arquivosPorConteudo, setArquivosPorConteudo] = useState({});
    const [loading, setLoading] = useState(false);
    const [erro404, setErro404] = useState(false);
    const [paginaAtual, setPaginaAtual] = useState(1);

    const itensPorPagina = 5;
    const navigate = useNavigate();

    useEffect(() => {
        const carregarConteudos = async () => {
            if (!tipoSelecionado) return;

            setLoading(true);
            setErro404(false);
            setConteudos([]);
            setArquivosPorConteudo({});
            setPaginaAtual(1);

            try {
                const res = await api.get(`/conteudos/tipoConteudo/${tipoSelecionado}`);
                const listaOrdenada = res.data.sort((a, b) =>
                    a.titulo.localeCompare(b.titulo, 'pt-BR', { sensitivity: 'base' })
                );
                setConteudos(listaOrdenada);

                const dadosArquivos = {};
                for (const conteudo of listaOrdenada) {
                    const resArq = await api.get(`/arquivos/conteudo/${conteudo.conteudoID}`);
                    const agrupado = {};
                    resArq.data.forEach((arquivo) => {
                        const tipo = arquivo.tipo || 6;
                        agrupado[tipo] = (agrupado[tipo] || 0) + 1;
                    });
                    dadosArquivos[conteudo.conteudoID] = agrupado;
                }

                setArquivosPorConteudo(dadosArquivos);
            } catch (err) {
                if (err.response?.status === 404) {
                    setErro404(true);
                    setConteudos([]);
                } else {
                    console.error('Erro ao buscar conteúdos:', err);
                    alert('Erro ao carregar conteúdos.');
                }
            } finally {
                setLoading(false);
            }
        };

        carregarConteudos();
    }, [tipoSelecionado]);

    const handleEditar = (conteudoID) => {
        navigate(`/admin/editar-conteudo/${conteudoID}`);
    };

    const indiceInicial = (paginaAtual - 1) * itensPorPagina;
    const conteudosPaginados = conteudos.slice(indiceInicial, indiceInicial + itensPorPagina);
    const totalPaginas = Math.ceil(conteudos.length / itensPorPagina);

    const gerarPaginas = () => {
        const paginas = [];
        if (totalPaginas <= 7) {
            for (let i = 1; i <= totalPaginas; i++) paginas.push(i);
        } else {
            paginas.push(1);
            if (paginaAtual > 3) paginas.push('...');
            for (
                let i = Math.max(2, paginaAtual - 1);
                i <= Math.min(totalPaginas - 1, paginaAtual + 1);
                i++
            ) {
                paginas.push(i);
            }
            if (paginaAtual < totalPaginas - 2) paginas.push('...');
            paginas.push(totalPaginas);
        }
        return paginas;
    };

    return (
        <div className="container mt-4">
            <h2>Listagem de Conteúdos</h2>

            <div className="mb-4">
                <label htmlFor="tipoConteudo" className="form-label">Tipo de Conteúdo</label>
                <select
                    id="tipoConteudo"
                    className="form-select"
                    value={tipoSelecionado}
                    onChange={(e) => setTipoSelecionado(e.target.value)}
                    style={{ fontWeight: 'bold', color: '#202135' }}
                >
                    <option value="">Selecione</option>
                    {Object.entries(tiposConteudoLabel).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                </select>
            </div>

            {loading && <p>Carregando conteúdos...</p>}

            {!loading && erro404 && (
                <p className="text-danger">Nenhum conteúdo encontrado com o tipo selecionado.</p>
            )}

            {!loading && !erro404 && conteudosPaginados.length > 0 && (
                <>
                    <div className="list-group">
                        {conteudosPaginados.map((conteudo) => (
                            <div key={conteudo.conteudoID} className="list-group-item mb-3 border rounded p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className='header-list-conteudo'>
                                        <h5 className="mb-1">{conteudo.titulo}</h5>
                                        <i><p className="mb-2 text-muted subtitleConteudo">{conteudo.observacao}</p></i>
                                    </div>
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => handleEditar(conteudo.conteudoID)}
                                    >
                                        <i className="fas fa-edit me-1"></i> Editar
                                    </button>
                                </div>

                                <div className="mt-2">
                                    <strong>Arquivos:</strong>
                                    <ul className="mt-1 mb-0 lista-arquivos">
                                        {Object.entries(arquivosPorConteudo[conteudo.conteudoID] || {}).map(
                                            ([tipo, quantidade]) => (
                                                <li key={tipo}>
                                                    {tiposArquivosLabel[tipo] || 'Outro'}: {quantidade}
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Paginação compacta */}
                    {totalPaginas > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <nav>
                                <ul className="pagination">
                                    {gerarPaginas().map((p, i) => (
                                        <li
                                            key={i}
                                            className={`page-item ${p === paginaAtual ? "active" : ""} ${p === "..." ? "disabled" : ""}`}
                                            onClick={() => typeof p === "number" && setPaginaAtual(p)}
                                        >
                                            <button className="page-link">{p}</button>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CadListaConteudos;
